import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { api, HANDSHAKE_EXPIRED_MESSAGE } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';

const TOKEN_KEY = 'ggm_portal_token';
const ENTRY_ORG_KEY = 'ggm_entry_org';

export function Handshake() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Which blob we have already sent a redeem for. A ref, not state: it must not
  // trigger a render, and it must survive one.
  const redeemedRef = useRef<string | null>(null);

  const blob = params.get('t') ?? '';
  // GATE repoint (dual-state, reversible): default-less handshake links — i.e.
  // /ggportal — now land on the GATE instead of the dashboard. Deep links that
  // set returnTo explicitly are unaffected. Flip this default back to '/' to
  // revert. Tapestry 12-gganvil-gate-onboarding (T-gate-fe / T-rollout).
  const rawReturnTo = params.get('returnTo') ?? '/gate';
  const returnTo = rawReturnTo.startsWith('/') ? rawReturnTo : '/gate';

  useEffect(() => {
    if (!blob) {
      setError('No handshake token in URL.');
      return;
    }
    // Fire the redeem EXACTLY ONCE per blob. The ref — not a per-effect-run
    // `cancelled` flag — is the single-fire guard, and that distinction is
    // load-bearing: under React 18 StrictMode (and any effect re-run) the
    // effect double-invokes mount→cleanup→mount. A `cancelled` cleanup would
    // set cancelled=true on the ONE in-flight redeem, while the remount is
    // blocked from re-firing it by this ref — so the redeem's response (success
    // OR error) was silently dropped and the user was stranded on /handshake
    // with no redirect and no error. (Regression from f039770's ref+cancelled
    // combo; caught by e2e auth.spec.ts.) The backend redeem is idempotent
    // (shared/handshake.ts) and React 18 makes a post-unmount setState/navigate
    // a harmless no-op, so processing the single response unconditionally is
    // correct — one request per link, one result handled.
    if (redeemedRef.current === blob) return;
    redeemedRef.current = blob;

    api.redeemHandshake(blob)
      .then(res => {
        localStorage.setItem(TOKEN_KEY, res.token);
        // T07: entering via an org's Discord scopes the portal to that org
        // for this session. Cleared by "Explore all" in the comp switcher.
        try {
          if (res.entryOrgId) {
            sessionStorage.setItem(ENTRY_ORG_KEY, JSON.stringify({ orgId: res.entryOrgId, orgName: res.entryOrgName ?? res.entryOrgId }));
          } else {
            sessionStorage.removeItem(ENTRY_ORG_KEY);
          }
          // WorkspaceProvider mounted before this write — tell it to re-read.
          window.dispatchEvent(new Event('ggm-entry-org-changed'));
        } catch { /* ignore */ }
        qc.invalidateQueries({ queryKey: ['me'] });
        window.history.replaceState({}, '', returnTo);
        navigate(returnTo, { replace: true });
      })
      .catch(err => {
        // W8: api.redeemHandshake collapses every dead-link case to the same
        // clear message — this fallback only fires if something throws with no
        // message at all.
        setError(err?.message ?? HANDSHAKE_EXPIRED_MESSAGE);
      });
  }, [blob, returnTo, navigate, qc]);

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12">
        <Card className="border-red-700/40">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 shrink-0 text-red-400" />
            <div>
              <h1 className="font-display text-lg font-black uppercase tracking-wider text-red-200">Link unusable</h1>
              <p className="mt-2 text-sm text-iron-200">{error}</p>
              <p className="mt-3 text-xs text-iron-500">
                Run /ggportal in Discord to get a fresh link.
              </p>
              <Link to="/" className="mt-4 inline-block font-display text-[11px] font-black uppercase tracking-wider text-forge-300 hover:text-forge-200">
                ← Back to portal
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return <PageSpinner />;
}
